import { BaseComponent } from '../../base-component/base-component';
import { CustomStore } from './interfaces/custom-store.interface';
import { ProductCardData } from '../../product-card/product-card-display';
import { customElement, property, state } from 'lit/decorators.js';
import { getPaginationDetails, getPaginationMessage } from '../../pagination/utils/pagination.utils';
import { html, nothing, TemplateResult } from 'lit';
import { mapCustomStoreProductsToProductCardData } from './mappings/custom-product.mapping';
import { CUSTOM_STORE_LIST_CARD_VARIATION } from '../utils/create-custom-product.util';

@customElement('mhecomm-custom-store-product-list')
export class CustomStoreProductList extends BaseComponent {
  @property({ type: Object })
  customStore?: CustomStore;

  @property({ type: Number })
  pageSize = 10;

  @state()
  private currentPage = 0;

  @state()
  private products: ProductCardData[] = [];

  private firstOfPagination = 1;
  private lastOfPagination = 1;

  connectedCallback(): void {
    super.connectedCallback();
    (this.shadowRoot as EventTarget | null)?.addEventListener('go-to-page-in-memory', this.handlePageChange.bind(this));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    (this.shadowRoot as EventTarget | null)?.removeEventListener('go-to-page-in-memory', this.handlePageChange.bind(this));
  }

  protected async updated(_changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
    super.updated(_changedProperties);
    if (_changedProperties.has('customStore')) {
      if (this.customStore) {
        this.products = mapCustomStoreProductsToProductCardData(this.customStore?.products || [], this.customStore);
        this.updatePaginationData();
      }
    }
  }

  renderComp(): TemplateResult | typeof nothing {
    return html`
      <div class="mb-4">
        ${this.renderPaginationMessage()}
      </div>
      ${this.getProductsToRender().map(
        (product, index) =>
          html`
            <mhecomm-create-custom-product-card-display
              .product="${product}"
              .productIndex="${index}"
              variation="${CUSTOM_STORE_LIST_CARD_VARIATION}"
            >
            </mhecomm-create-custom-product-card-display>
          `
      )}
      ${this.renderPaginationInMemory()}
    `;
  }

  /**
   * Returns the products to render based on the current pagination.
   */
  getProductsToRender(): ProductCardData[] {
    const newList: ProductCardData[] = [];

    for (let i = this.firstOfPagination - 1; i < this.lastOfPagination; i++) {
      const product: ProductCardData | undefined = this.products[i];
      if (product) {
        newList.push(product);
      }
    }

    return newList;
  }

  /**
   * Renders the pagination message at the top of the list.
   */
  private renderPaginationMessage(): TemplateResult | typeof nothing {
    if (!this.hasPagination()) {
      return nothing;
    }

    return html`
      <div class="d-flex">
        <mhecomm-pagination-message
          ?useRawMessage=${true}
          data-automation-id=${`${this.id}-pagination_message`}
          id=${`${this.id}-pagination_message`}
          message="${getPaginationMessage(this.currentPage, this.pageSize, this.getCustomProductsCount())}"
        >
        </mhecomm-pagination-message>
      </div>
    `;
  }

  /**
   * Renders the pagination and pagination message at the bottom of the list.
   */
  renderPaginationInMemory(): TemplateResult {
    return html`
      <mhecomm-pagination-in-memory
        .currentPage=${this.currentPage}
        .pageLength=${this.pageSize}
        .total=${this.getCustomProductsCount()}
        data-automation-id=${`${this.id}-pagination`}
        id=${`${this.id}-pagination`}
        maxPagesShown="3"
      ></mhecomm-pagination-in-memory>
    `;
  }

  /**
   * It should be used when the pagination occurs on the pagination-in-memory.
   */
  private updatePaginationData(paginationNumber?: number): void {
    const page = paginationNumber ? paginationNumber - 1 : this.currentPage;
    const { currentPage, firstOfPage, lastOfPage } = getPaginationDetails(
      page,
      this.pageSize,
      this.getCustomProductsCount()
    );
    this.firstOfPagination = firstOfPage;
    this.lastOfPagination = lastOfPage;
    this.currentPage = currentPage;
  }

  private handlePageChange(e: CustomEvent): void {
    this.updatePaginationData(e.detail.paginationNumber);
  }

  /**
   * Checks if the custom store product list has pagination.
   */
  private hasPagination(): boolean {
    return this.products && this.products.length > this.pageSize;
  }

  /**
   * Returns the total custom products count.
   */
  private getCustomProductsCount(): number {
    return this.products.length ?? 0;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-custom-store-product-list': CustomStoreProductList;
  }
}
